import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';

import CatalogAttribute from '@/components/catalog-attributes/CatalogAttribute';
import { Icon } from '@/components/core/Icon';
import { AttributeGridCell } from '@/components/datamodel/AttributeGridCell';
import {
    DataModelAttribute,
    DataModelAttributeTypeName,
    PersistedItem,
} from '@/types/DataModel';

import { useCellEditableCondition } from './hooks/useCellEditableCondition';
import { useEditingRow } from './hooks/useEditingRow';

/**
 * Placeholder icon shown in read-only browse cells whose asset value is
 * empty. Mirrors the legacy AngularJS grid, which rendered a small photo /
 * document glyph so the user could see the cell is an asset slot before
 * double-clicking to edit.
 */
const getEmptyAssetIcon = (attribute: DataModelAttribute) => {
    switch (attribute.typeName || attribute.baseClass) {
        case DataModelAttributeTypeName.Image:
            return CloudArrowUpIcon;
        case DataModelAttributeTypeName.Document:
            return CloudArrowUpIcon;
        default:
            return undefined;
    }
};

interface EditableItemCellProps {
    attribute: DataModelAttribute;
    item: PersistedItem;
}

/**
 * Build the string used for the native browser tooltip on hover. We keep
 * this narrow on purpose: for primitive values (string / number /
 * boolean) the raw item value is exactly what CatalogAttribute renders
 * in most attribute types, so it matches the visible-then-truncated
 * text. For structured values (arrays, objects, references) we fall
 * back to no title rather than dumping `[object Object]` on the user;
 * those cells are typically icons or nested tables where the tooltip
 * wouldn't add anything anyway.
 */
const getCellTooltip = (
    item: PersistedItem,
    attribute: DataModelAttribute,
): string | undefined => {
    const value = item[attribute.name];
    if (value == null) return undefined;
    if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    ) {
        return String(value);
    }
    return undefined;
};

/**
 * Grid cell used by the browse items list. Renders a read-only
 * `<CatalogAttribute>` by default and swaps to an `<AttributeGridCell>`
 * (form-bound editor) once the row it belongs to is put into edit mode.
 *
 * The read-only branch matches the previous `ItemsListCatalogCell` output
 * exactly so the browse grid keeps its familiar look until the user
 * double-clicks a cell.
 */
const CoreEditableItemCell = ({ attribute, item }: EditableItemCellProps) => {
    const editingRow = useEditingRow();
    const { editable, hidden, readonly } = useCellEditableCondition(
        item,
        attribute,
    );

    const isThisRowBeingEdited =
        editingRow?.editingRowId === item.primaryKey__ &&
        editingRow?.editingRowId !== null;

    const handleDoubleClick = () => {
        // Legacy laxGridStartCellEdit: enter edit mode on double-click.
        // The cell falls back to read-only when it can't be edited.
        if (!editable) return;
        editingRow?.beginEdit(item);
    };

    const handleFocus = () => {
        // Same as laxGridStartCellEdit for keyboard navigation / focus:
        // once the user is inside an editor, the row is actively being
        // edited so the store queue must hold off (`shouldSkip`).
        if (!editingRow) return;
        if (!editable) return;
        editingRow.isEditingActiveRef.current = true;
    };

    const handleBlur = () => {
        // Legacy laxGridStopCellEdit: clear rowEditingActive then
        // force-flush the store queue for immediate save.
        editingRow?.endEdit();
    };

    if (hidden) {
        // isGridAttributeHidden: nothing visible for this cell.
        return null;
    }

    if (!isThisRowBeingEdited) {
        // Read-only path: match the previous `ItemsListCatalogCell` shape
        // (render nothing when the item has no value for the attribute) but
        // always give the wrapper an explicit minimum height so empty cells
        // remain valid double-click targets. Without `tw-min-h-*` both this
        // div and the intermediate GridCell wrapper collapse to 0px on
        // fields like an empty `string_fixed_5`, silently swallowing the
        // event and making the cell impossible to enter.
        //
        // Long values are collapsed to a single line with an ellipsis
        // (`tw-truncate` + `singleLine` on CatalogAttribute). The native
        // `title` gives the full value on hover, and resizing the column
        // wider automatically reveals more text because the truncation
        // is driven by the cell width, not a hardcoded character count.
        // Only applied to the browse list; the catalog view uses
        // ItemsListCatalogCell (see itemsList.tsx) and is untouched.
        const hasValue = Boolean(item[attribute.name]);
        const emptyAssetIcon = hasValue ? undefined : getEmptyAssetIcon(attribute);
        const tooltip = hasValue ? getCellTooltip(item, attribute) : undefined;
        return (
            <div
                onDoubleClick={handleDoubleClick}
                title={tooltip}
                className="tw-h-full tw-w-full tw-min-h-[24px] tw-truncate"
            >
                {hasValue && (
                    <CatalogAttribute
                        attribute={attribute}
                        item={item}
                        withoutLabel={true}
                        gridMode={true}
                        singleLine={true}
                    />
                )}
                {emptyAssetIcon && (
                    <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
                        <Icon
                            icon={emptyAssetIcon}
                            size="sm"
                            className='default-icon-button'
                        />
                    </div>
                )}
            </div>
        );
    }

    // Editing path: rely on the row-level FormProvider mounted by
    // EditableRowFormProvider. `AttributeGridCell` uses the shared
    // per-type editor switch (also used by Collection).
    //
    // The `data-editing-cell` attribute is a DOM anchor for the row-level
    // click-outside detection in `EditableRowFormProvider`: because a
    // React `<FormProvider>` has no DOM presence and we cannot inject a
    // wrapper element between `<tr>` and its `<td>` children, the provider
    // walks up from any editing cell to find the row's `<tr>` at mount.
    return (
        <div
            data-editing-cell="true"
            onFocusCapture={handleFocus}
            onBlurCapture={handleBlur}
            onDoubleClick={handleDoubleClick}
        >
            <AttributeGridCell
                attribute={attribute}
                fieldPath={attribute.name}
                rowData={item}
                readonly={readonly || !editable}
            />
        </div>
    );
};

/**
 * Memoized because the parent items list re-renders on every fetch and
 * we do not want the read-only cell (which does non-trivial rendering of
 * dates / options / images) to re-run when its own data hasn't changed.
 */
const EditableItemCell = memo(
    CoreEditableItemCell,
    (prev, next) =>
        prev.item === next.item &&
        prev.attribute.name === next.attribute.name,
);

export { EditableItemCell };
